/**
 * SyringeDetailModal Component
 *
 * Modal showing detailed information about a culture syringe.
 * Includes supplier info, volume tracking, usage history, and actions.
 * Fields are inline-editable following the SpawnBatchDetailModal pattern.
 */

import { useState, useCallback, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { Progress } from '@/components/ui/progress'
import { Separator } from '@/components/ui/separator'
import { InlineEditableField } from '@/components/ui/inline-editable-field'
import { useCultureSyringe, useCultureSyringes } from '@/hooks/useCultureSyringes'
import { toast } from 'sonner'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import {
  Syringe,
  Building2,
  Calendar,
  MapPin,
  Trash2,
  Loader2,
  ExternalLink,
  Clock,
  TestTube,
  Beaker,
  Pencil,
  Check,
  X,
} from 'lucide-react'

// Status badge variants
const getStatusBadge = (status, size = 'default') => {
  const baseClass = size === 'large' ? 'text-sm px-3 py-1' : ''

  switch (status) {
    case 'received':
      return <Badge variant="secondary" className={baseClass}>Received</Badge>
    case 'in_use':
      return <Badge variant="default" className={baseClass}>In Use</Badge>
    case 'exhausted':
      return <Badge variant="outline" className={baseClass}>Exhausted</Badge>
    case 'expired':
      return <Badge variant="destructive" className={baseClass}>Expired</Badge>
    case 'disposed':
      return <Badge variant="outline" className={`text-muted-foreground ${baseClass}`}>Disposed</Badge>
    default:
      return <Badge variant="outline" className={baseClass}>{status}</Badge>
  }
}

// Terminal statuses - editing disabled
const TERMINAL_STATUSES = ['disposed', 'exhausted']

export default function SyringeDetailModal({
  open,
  onOpenChange,
  syringeId
}) {
  const { syringe, isLoading, error, refetch } = useCultureSyringe(syringeId)
  const { updateSyringe, disposeSyringe } = useCultureSyringes()

  const [isSaving, setIsSaving] = useState(false)
  const [showDisposeDialog, setShowDisposeDialog] = useState(false)

  // Editable batch code state (like SpawnBatchDetailModal)
  const [isEditingCode, setIsEditingCode] = useState(false)
  const [editCodeValue, setEditCodeValue] = useState('')
  const [codeOverride, setCodeOverride] = useState(null)

  // Editable notes state (multi-line, separate from InlineEditableField)
  const [isEditingNotes, setIsEditingNotes] = useState(false)
  const [editedNotes, setEditedNotes] = useState('')

  const isTerminal = syringe ? TERMINAL_STATUSES.includes(syringe.status) : false
  const batchCode = syringe?.supplier_batch_code

  // Clear code override when prop catches up after refetch
  useEffect(() => {
    setCodeOverride(null)
  }, [batchCode])

  // Calculate expiry info
  const getExpiryInfo = () => {
    if (!syringe?.expiry_date) return { days: null, color: '', isExpired: false }

    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const expiry = new Date(syringe.expiry_date)
    const diffTime = expiry - today
    const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    let color = 'text-green-600'
    if (days <= 0) color = 'text-destructive'
    else if (days <= 7) color = 'text-amber-600'
    else if (days <= 14) color = 'text-amber-500'

    return { days, color, isExpired: days <= 0 }
  }

  const expiryInfo = syringe ? getExpiryInfo() : { days: null, color: '', isExpired: false }
  const volumePercent = syringe ? (syringe.volume_remaining_ml / syringe.volume_ml) * 100 : 0

  // Generic field save handler
  const handleFieldSave = useCallback(async (fieldName, value) => {
    if (!syringe?.id) return
    try {
      await updateSyringe(syringe.id, { [fieldName]: value })
      refetch()
    } catch (err) {
      toast.error(`Failed to update ${fieldName}`, { description: err.message })
    }
  }, [syringe?.id, updateSyringe, refetch])

  // Inline batch code editing
  const handleCodeEdit = useCallback(() => {
    if (isTerminal) return
    setEditCodeValue(codeOverride ?? batchCode ?? '')
    setIsEditingCode(true)
  }, [isTerminal, batchCode, codeOverride])

  const handleCodeSave = useCallback(async () => {
    const trimmed = editCodeValue.trim()
    setIsEditingCode(false)
    if (!trimmed || trimmed === batchCode) return

    setCodeOverride(trimmed)
    try {
      await updateSyringe(syringe?.id, { supplier_batch_code: trimmed })
      refetch()
    } catch (err) {
      toast.error('Failed to update batch code', { description: err.message })
      setCodeOverride(null)
    }
  }, [editCodeValue, batchCode, syringe?.id, updateSyringe, refetch])

  const handleCodeKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleCodeSave()
    } else if (e.key === 'Escape') {
      setIsEditingCode(false)
    }
  }, [handleCodeSave])

  // Notes editing handlers
  const handleStartEditNotes = () => {
    setEditedNotes(syringe?.notes || '')
    setIsEditingNotes(true)
  }

  const handleSaveNotes = async () => {
    setIsSaving(true)
    try {
      await updateSyringe(syringe.id, {
        notes: editedNotes.trim() || null,
      })
      toast.success('Notes updated')
      setIsEditingNotes(false)
      refetch()
    } catch (err) {
      toast.error('Failed to update notes', { description: err.message })
    } finally {
      setIsSaving(false)
    }
  }

  // Handle dispose
  const handleDispose = async () => {
    setIsSaving(true)
    try {
      await disposeSyringe(syringe.id)
      toast.success('Syringe disposed')
      setShowDisposeDialog(false)
      onOpenChange(false)
    } catch (err) {
      toast.error('Failed to dispose syringe', { description: err.message })
    } finally {
      setIsSaving(false)
    }
  }

  // Check if syringe can be disposed
  const canDispose = syringe && !['disposed', 'exhausted'].includes(syringe.status)
  const displayCode = codeOverride ?? batchCode

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-destructive">Failed to load syringe details</p>
              <p className="text-sm text-muted-foreground mt-1">{error}</p>
            </div>
          ) : syringe ? (
            <>
              {/* Header - editable batch code */}
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle className="flex items-center gap-2 text-xl font-mono">
                    <Syringe className="h-5 w-5" />
                    {isEditingCode ? (
                      <input
                        autoFocus
                        className="text-xl font-mono w-48 bg-transparent border-b border-primary outline-none"
                        value={editCodeValue}
                        onChange={(e) => setEditCodeValue(e.target.value)}
                        onBlur={handleCodeSave}
                        onKeyDown={handleCodeKeyDown}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={handleCodeEdit}
                        disabled={isTerminal}
                        className={cn(
                          "rounded px-1 -mx-1 transition-colors",
                          !isTerminal && "hover:bg-muted/50 cursor-pointer"
                        )}
                      >
                        {displayCode}
                      </button>
                    )}
                  </DialogTitle>
                  {getStatusBadge(syringe.status, 'large')}
                </div>
                <p className="text-muted-foreground text-sm">
                  {syringe.strain?.name} ({syringe.strain?.code})
                </p>
              </DialogHeader>

              <div className="flex-1 overflow-y-auto min-h-0 space-y-4 -mx-6 px-6 py-2">
                {/* Dates - inline editable */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Dates
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <InlineEditableField
                        label="Received"
                        type="date"
                        value={syringe.received_date}
                        onSave={(val) => handleFieldSave('received_date', val)}
                        disabled={isTerminal}
                      />
                      <InlineEditableField
                        label="Expires"
                        type="date"
                        value={syringe.expiry_date}
                        onSave={(val) => handleFieldSave('expiry_date', val)}
                        disabled={isTerminal}
                      />
                    </div>
                    {expiryInfo.days !== null && (
                      <p className={`text-xs font-medium mt-2 ${expiryInfo.color}`}>
                        {expiryInfo.isExpired
                          ? 'Expired'
                          : `${expiryInfo.days} days until expiry`}
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Volume - inline editable */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <TestTube className="h-4 w-4" />
                      Volume
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <InlineEditableField
                        label="Total"
                        type="number"
                        value={syringe.volume_ml}
                        suffix="ml"
                        onSave={(val) => handleFieldSave('volume_ml', val)}
                        disabled={isTerminal}
                      />
                      <InlineEditableField
                        label="Remaining"
                        type="number"
                        value={syringe.volume_remaining_ml}
                        suffix="ml"
                        onSave={(val) => handleFieldSave('volume_remaining_ml', val)}
                        disabled={isTerminal}
                      />
                    </div>
                    <div className="mt-3 space-y-1">
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{syringe.volume_remaining_ml}ml of {syringe.volume_ml}ml</span>
                        <span className={volumePercent <= 25 ? 'text-amber-600 font-medium' : ''}>
                          {Math.round(volumePercent)}%
                        </span>
                      </div>
                      <Progress value={volumePercent} className="h-2" />
                    </div>
                  </CardContent>
                </Card>

                {/* Storage & Notes */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Storage & Notes
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <InlineEditableField
                      label="Storage Location"
                      type="text"
                      value={syringe.storage_location}
                      onSave={(val) => handleFieldSave('storage_location', val)}
                      disabled={isTerminal}
                      placeholder="Click to add location"
                    />
                    {/* Notes - multi-line textarea */}
                    <div className="space-y-1">
                      <span className="text-xs text-muted-foreground">Notes</span>
                      {isEditingNotes ? (
                        <div className="space-y-2">
                          <Textarea
                            value={editedNotes}
                            onChange={(e) => setEditedNotes(e.target.value)}
                            placeholder="Additional notes..."
                            rows={3}
                            autoFocus
                          />
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setIsEditingNotes(false)}
                              disabled={isSaving}
                            >
                              <X className="h-4 w-4 mr-1" />
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={handleSaveNotes}
                              disabled={isSaving}
                            >
                              {isSaving ? (
                                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                              ) : (
                                <Check className="h-4 w-4 mr-1" />
                              )}
                              Save
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={handleStartEditNotes}
                          disabled={isTerminal}
                          className={cn(
                            "w-full text-left text-sm rounded-md px-2 py-1 -mx-2 transition-colors",
                            isTerminal
                              ? "text-muted-foreground cursor-not-allowed opacity-60"
                              : "hover:bg-muted/50 cursor-pointer"
                          )}
                        >
                          {syringe.notes ? (
                            <p className="whitespace-pre-wrap">{syringe.notes}</p>
                          ) : (
                            <span className="text-muted-foreground italic">Click to add notes</span>
                          )}
                        </button>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Supplier Card - read-only */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Supplier
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Name</span>
                      <span className="font-medium">{syringe.supplier?.name}</span>
                    </div>
                    {syringe.supplier?.contact_name && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Contact</span>
                        <span>{syringe.supplier.contact_name}</span>
                      </div>
                    )}
                    {syringe.supplier?.email && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Email</span>
                        <a
                          href={`mailto:${syringe.supplier.email}`}
                          className="text-primary hover:underline"
                        >
                          {syringe.supplier.email}
                        </a>
                      </div>
                    )}
                    {syringe.supplier?.phone && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Phone</span>
                        <span>{syringe.supplier.phone}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Usage History Card */}
                {((syringe.inoculated_lcs && syringe.inoculated_lcs.length > 0) ||
                  (syringe.inoculated_spawns && syringe.inoculated_spawns.length > 0)) && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Used On ({(syringe.inoculated_lcs?.length || 0) + (syringe.inoculated_spawns?.length || 0)})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {/* Liquid Cultures Section */}
                        {syringe.inoculated_lcs && syringe.inoculated_lcs.length > 0 && (
                          <div className="space-y-2">
                            <div className="text-xs text-muted-foreground font-medium uppercase tracking-wide flex items-center gap-1.5">
                              <TestTube className="h-3 w-3" />
                              Liquid Cultures ({syringe.inoculated_lcs.length})
                            </div>
                            {syringe.inoculated_lcs.map((lc) => (
                              <div
                                key={lc.id}
                                className="flex items-center justify-between text-sm py-1"
                              >
                                <Link
                                  to={`/production/liquid-culture/${lc.id}`}
                                  className="font-mono text-primary hover:underline flex items-center gap-1"
                                  onClick={() => onOpenChange(false)}
                                >
                                  {lc.culture_code}
                                  <ExternalLink className="h-3 w-3" />
                                </Link>
                                <div className="flex items-center gap-2 text-muted-foreground">
                                  <span>G{lc.generation}</span>
                                  <span>•</span>
                                  <span>
                                    {new Date(lc.inoculation_date).toLocaleDateString('en-GB', {
                                      day: 'numeric',
                                      month: 'short',
                                    })}
                                  </span>
                                  <Badge variant="outline" className="text-xs">
                                    {lc.status}
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Spawn Batches Section */}
                        {syringe.inoculated_spawns && syringe.inoculated_spawns.length > 0 && (
                          <div className="space-y-2">
                            <div className="text-xs text-muted-foreground font-medium uppercase tracking-wide flex items-center gap-1.5">
                              <Beaker className="h-3 w-3" />
                              Spawn Batches ({syringe.inoculated_spawns.length})
                            </div>
                            {syringe.inoculated_spawns.map((spawn) => (
                              <div
                                key={spawn.id}
                                className="flex items-center justify-between text-sm py-1"
                              >
                                <Link
                                  to={`/production/spawn/${spawn.id}`}
                                  className="font-mono text-primary hover:underline flex items-center gap-1"
                                  onClick={() => onOpenChange(false)}
                                >
                                  {spawn.code}
                                  <ExternalLink className="h-3 w-3" />
                                </Link>
                                <div className="flex items-center gap-2 text-muted-foreground">
                                  <span>{spawn.bag_count} bags</span>
                                  <span>•</span>
                                  <span>
                                    {new Date(spawn.inoculation_date).toLocaleDateString('en-GB', {
                                      day: 'numeric',
                                      month: 'short',
                                    })}
                                  </span>
                                  <Badge variant="outline" className="text-xs">
                                    {spawn.status}
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Separator />

                {/* Actions */}
                {canDispose && (
                  <div className="flex justify-end">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setShowDisposeDialog(true)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Dispose Syringe
                    </Button>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Dispose Confirmation Dialog */}
      <AlertDialog open={showDisposeDialog} onOpenChange={setShowDisposeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Dispose Syringe?</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark the syringe as disposed. This action cannot be undone.
              {syringe?.volume_remaining_ml > 0 && (
                <span className="block mt-2 text-amber-600">
                  Note: This syringe still has {syringe.volume_remaining_ml}ml remaining.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDispose}
              disabled={isSaving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Dispose
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
